import {
  AssignScreenerPayload,
  AutoDistributePayload,
  PullFromDrawPayload,
  ScreenerQueueFilter,
  SubmitQCReviewPayload,
  SubmitScreeningDecisionPayload,
  UnassignScreenerPayload,
  UpdateBriefDecisionPayload,
  UpdateQueueStatusPayload,
} from '@tsin-core/services/http/screener-queue.service';

/** Load paginated screener queue / Charger la file d'attente des examinateurs */
export class LoadScreenerQueue {
  static readonly type = '[ScreenerQueue] Load Queue';
  constructor(public filter: ScreenerQueueFilter = {}) {}
}

/** Load a single application from the queue / Charger une candidature de la file */
export class LoadScreenerQueueApplication {
  static readonly type = '[ScreenerQueue] Load Application';
  constructor(public id: string) {}
}

/** Load screener workload stats / Charger les statistiques de charge de travail */
export class LoadScreenerStats {
  static readonly type = '[ScreenerQueue] Load Stats';
  constructor(public cohortId?: string) {}
}

/** Assign a screener manually / Assigner manuellement un examinateur */
export class AssignScreener {
  static readonly type = '[ScreenerQueue] Assign Screener';
  constructor(
    public id: string,
    public payload: AssignScreenerPayload,
  ) {}
}

/** Unassign the current screener / Désassigner l'examinateur actuel */
export class UnassignScreener {
  static readonly type = '[ScreenerQueue] Unassign Screener';
  constructor(
    public id: string,
    public payload: UnassignScreenerPayload = {},
  ) {}
}

/** Update queue status / Mettre à jour le statut dans la file */
export class UpdateQueueStatus {
  static readonly type = '[ScreenerQueue] Update Status';
  constructor(
    public id: string,
    public payload: UpdateQueueStatusPayload,
  ) {}
}

/** Auto-distribute applications to screeners / Distribuer automatiquement */
export class AutoDistribute {
  static readonly type = '[ScreenerQueue] Auto Distribute';
  constructor(public payload: AutoDistributePayload) {}
}

/** Set the filter used for the screener queue / Définir le filtre de la file */
export class SetScreenerQueueFilter {
  static readonly type = '[ScreenerQueue] Set Filter';
  constructor(public filter: ScreenerQueueFilter) {}
}

/** Clear queue state / Effacer l'état de la file */
export class ClearScreenerQueue {
  static readonly type = '[ScreenerQueue] Clear';
}

// ─── QC Workflow Actions ─────────────────────────────────────────────────────

/** Load QC review queue / Charger la file de révision AQ */
export class LoadQCQueue {
  static readonly type = '[QCWorkflow] Load QC Queue';
  constructor(public filter: { cohortId?: string; phase?: number } = {}) {}
}

/** Load CD review queue / Charger la file de révision du directeur clinique */
export class LoadCDReviewQueue {
  static readonly type = '[QCWorkflow] Load CD Review Queue';
  constructor(public cohortId?: string) {}
}

/** Load manager review queue — applications with preliminary decisions awaiting final sign-off.
 *  Charger la file de révision du gestionnaire — candidatures avec décisions préliminaires en attente. */
export class LoadManagerReviewQueue {
  static readonly type = '[QCWorkflow] Load Manager Review Queue';
  constructor(public filter: { cohortId?: string; phase?: number } = {}) {}
}

/** Load a single application for QC review / Charger une candidature pour révision AQ */
export class LoadQCApplication {
  static readonly type = '[QCWorkflow] Load Application';
  constructor(public id: string) {}
}

/** Start a QC review session / Démarrer une session de révision AQ */
export class InitiateQCReview {
  static readonly type = '[QCWorkflow] Initiate Review';
  constructor(public id: string) {}
}

/** Submit a completed QC review / Soumettre une révision AQ complète */
export class SubmitQCReview {
  static readonly type = '[QCWorkflow] Submit Review';
  constructor(
    public id: string,
    public payload: SubmitQCReviewPayload,
  ) {}
}

/** CD accepts, rejects, or manually writes brief / Décision du Directeur clinique */
export class UpdateBriefDecision {
  static readonly type = '[QCWorkflow] Update Brief Decision';
  constructor(
    public id: string,
    public payload: UpdateBriefDecisionPayload,
  ) {}
}

/** Phase 1D — Finalize (publish) the most recent preliminary decision as the official final decision.
 *  Phase 1D — Finaliser (publier) la dernière décision préliminaire en tant que décision officielle finale. */
export class FinalizeDecision {
  static readonly type = '[QCWorkflow] Finalize Decision';
  constructor(
    public id: string,
    public payload: { noteEn?: string; noteFr?: string } = {},
  ) {}
}

// ─── B4: AI Screening Brief Actions ─────────────────────────────────────────

/** Load (or generate) the screener re-entry brief for an application.
 *  Charger (ou générer) le résumé de reprise pour l'examinateur. */
export class LoadReentryBrief {
  static readonly type = '[ScreeningBrief] Load Re-entry Brief';
  constructor(public applicationId: string) {}
}

// ─── B5: AI Document Validation Actions ──────────────────────────────────────

/** Trigger AI document validation (photo + ID) for an application.
 *  Déclencher la validation IA des documents (photo + pièce d'identité). */
export class TriggerDocumentValidation {
  static readonly type = '[AIValidation] Trigger Document Validation';
  constructor(public id: string) {}
}

// ─── B6: Resume Template Compliance Actions ───────────────────────────────────

/** Trigger AI resume template compliance check for an application.
 *  Déclencher la vérification de conformité du modèle de CV par IA. */
export class TriggerResumeValidation {
  static readonly type = '[AIValidation] Trigger Resume Validation';
  constructor(public id: string) {}
}

/** Dismiss the re-entry brief banner (local UI state only).
 *  Masquer la bannière du résumé de reprise (état local uniquement). */
export class DismissReentryBrief {
  static readonly type = '[ScreeningBrief] Dismiss Re-entry Brief';
}

// ─── Screening Workflow Actions ───────────────────────────────────────────────

/** Pull all applications from a PortalDraw into the screener queue (set status = Pending).
 *  Activer toutes les candidatures d'un tirage dans la file d'attente (statut = Pending). */
export class PullFromDraw {
  static readonly type = '[ScreenerQueue] Pull From Draw';
  constructor(public payload: PullFromDrawPayload) {}
}

/** Manually (re-)trigger KYE agents for ALL applications in an activated draw.
 *  force=true bypasses dedup and issues a fresh run for each app.
 *  retrospective=true instructs the webhook mapper to store the audit record only
 *  and NOT overwrite the ProUserApplication document — use for past/closed cohorts.
 *
 *  Déclencher manuellement les agents KYE pour toutes les candidatures d'un tirage activé.
 *  force=true contourne le dédup ; retrospective=true sauvegarde l'audit sans écraser la candidature. */
export class TriggerKyeForDraw {
  static readonly type = '[ScreenerQueue] Trigger KYE For Draw';
  constructor(
    public drawId: string,
    public force = false,
    public retrospective = false,
  ) {}
}

/** Submit a preliminary screening decision and route the application.
 *  Soumettre une décision d'examen préliminaire et acheminer la candidature. */
export class SubmitScreeningDecision {
  static readonly type = '[ScreenerQueue] Submit Screening Decision';
  constructor(
    public id: string,
    public payload: SubmitScreeningDecisionPayload,
  ) {}
}

/** Load a single application for the processing detail view.
 *  Charger une candidature pour la vue de détail de traitement. */
export class LoadApplicationForProcessing {
  static readonly type = '[ScreenerQueue] Load Application For Processing';
  constructor(public id: string) {}
}
